import { useNetworkStore } from '@repletoryreach/sdk-utils/store';
import { buildUrl, type QueryObject } from '@repletoryreach/sdk-utils/url';
import { getConfig } from './config.js';
import { ApiError, NetworkError, TimeoutError, fromErrorBody } from './errors.js';
import { generateIdempotencyKey } from './idempotency.js';
import { triggerRefresh } from './refresh.js';
import { readAccessToken, clearTokens } from './tokens.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  query?: QueryObject;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  idempotencyKey?: string;
  auth?: boolean;
  timeoutMs?: number;
  /** Return the raw response body instead of parsing it as JSON. */
  blob?: boolean;
}

interface InternalRequestOptions extends RequestOptions {
  _retried?: boolean;
}

const WRITE_METHODS: ReadonlySet<HttpMethod> = new Set(['POST', 'PUT', 'PATCH']);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelay(attempt: number, base: number, jitter: boolean): number {
  const exp = base * Math.pow(2, attempt);
  return jitter ? exp * (0.5 + Math.random() * 0.5) : exp;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function doFetch(
  method: HttpMethod,
  path: string,
  opts: InternalRequestOptions
): Promise<Response> {
  const cfg = getConfig();
  const fetchImpl = cfg.fetchImpl ?? globalThis.fetch;
  const url = buildUrl({ base: cfg.apiBaseUrl, path, query: opts.query });

  const headers: Record<string, string> = {
    accept: 'application/json',
    ...opts.headers
  };
  if (opts.body !== undefined && opts.body !== null && !(opts.body instanceof FormData)) {
    headers['content-type'] = headers['content-type'] ?? 'application/json';
  }
  if (opts.auth !== false) {
    const token = readAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  if (WRITE_METHODS.has(method) && cfg.http.idempotency === 'auto' && !headers['idempotency-key']) {
    headers['idempotency-key'] = opts.idempotencyKey ?? generateIdempotencyKey();
  }

  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? cfg.http.timeoutMs;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: cfg.http.credentials,
    signal: controller.signal
  };
  if (opts.body !== undefined && opts.body !== null) {
    init.body = opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body);
  }

  try {
    return await fetchImpl(url, init);
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError' && !opts.signal?.aborted) {
      throw new TimeoutError(timeoutMs);
    }
    throw new NetworkError((e as Error).message ?? 'Network request failed', e);
  } finally {
    clearTimeout(timer);
  }
}

export async function request<T>(
  method: HttpMethod,
  path: string,
  opts: InternalRequestOptions = {}
): Promise<T> {
  const cfg = getConfig();
  const exec = async (): Promise<T> => {
    let attempt = 0;
    for (;;) {
      const res = await doFetch(method, path, opts);

      if (res.status === 401 && opts.auth !== false && cfg.auth.refreshOn401 && !opts._retried) {
        const newToken = await triggerRefresh();
        if (newToken) {
          return request<T>(method, path, { ...opts, _retried: true });
        }
        cfg.onAuthExpired?.();
        clearTokens();
        const body = await readBody(res);
        const err = fromErrorBody(401, body);
        cfg.onApiError?.(err);
        throw err;
      }

      if (res.status === 429 && attempt < cfg.http.retry.attempts) {
        const retryAfter = res.headers.get('retry-after');
        const delay = retryAfter
          ? Number(retryAfter) * 1000
          : backoffDelay(attempt, cfg.http.retry.baseDelayMs, cfg.http.retry.jitter);
        attempt += 1;
        await sleep(delay);
        continue;
      }

      if (res.status >= 500 && attempt < cfg.http.retry.attempts && method === 'GET') {
        const delay = backoffDelay(attempt, cfg.http.retry.baseDelayMs, cfg.http.retry.jitter);
        attempt += 1;
        await sleep(delay);
        continue;
      }

      /*
       * `blob: true` skips `readBody`, which parses JSON. A file download has
       * to stay bytes: an export of 200 000 contacts must not be routed
       * through a JSON parse, and a CSV is not JSON in any case. Errors still
       * go through the normal path, because a failure IS an error envelope.
       */
      if (opts.blob && res.ok) return (await res.blob()) as T;

      const body = await readBody(res);
      if (!res.ok) {
        const err = fromErrorBody(res.status, body);
        cfg.onApiError?.(err);
        throw err;
      }
      return body as T;
    }
  };

  return useNetworkStore.getState().withTracking(exec());
}

export const http = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, opts?: RequestOptions) => request<T>('POST', path, opts),
  put: <T>(path: string, opts?: RequestOptions) => request<T>('PUT', path, opts),
  patch: <T>(path: string, opts?: RequestOptions) => request<T>('PATCH', path, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
  /** GET that returns the raw bytes: file downloads, not JSON. */
  getBlob: (path: string, opts?: RequestOptions) => request<Blob>('GET', path, { ...opts, blob: true })
};
